import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ALLOWED_FILES_COUNT, TOKEN_KEY } from '@/consts/const';
import { convertHeicFilesToWebp, getConvertedToWebpImages, isHeicLikeFile } from '@/utils/getConvertedToWebpImages';
import { sendPreRequestAuditIfMatched } from '@/utils/audit/sendPreRequestAudit';
import { refreshAccessToken } from '@/utils/auth/refreshAccessToken';
import type { FinalizeUploadRequest, PresignedDataResponse, UploadFileMeta, UploadStatus } from './types';
import { getFileExtension } from './utils/getFileExtension';

function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

interface UploadFileContextType {
  uploads: UploadFileMeta[];
  files: File[];
  entityLabel?: string;
  onSelectFiles: (files: File[], entityId?: string) => void;
  onDeleteFile: (file: File) => void;
  onDeleteAllFiles: VoidFunction;
  onUploadFiles: (finalizeUploadRequest: FinalizeUploadRequest) => Promise<void>;
  onCancelUpload: (fileName: string) => void;
  onCancelAllUploads: VoidFunction;
  onOpenUploadManager: (param?: string) => void;
  onCloseUploadManager: VoidFunction;
  onCropImage: (blob: Blob, fileName: string) => void;
  isPendingFilesConversion: boolean;
  isOpenUploadManager: boolean;
  isDeprecated: boolean;
}

export const UploadFileContext = createContext<UploadFileContextType>({
  uploads: [],
  files: [],
  onDeleteFile: () => {},
  onDeleteAllFiles: () => {},
  onSelectFiles: () => {},
  onUploadFiles: () => Promise.resolve(),
  onCancelUpload: () => {},
  onCancelAllUploads: () => {},
  onOpenUploadManager: () => {},
  onCloseUploadManager: () => {},
  onCropImage: () => {},
  isOpenUploadManager: false,
  isPendingFilesConversion: false,
  isDeprecated: false,
});

interface Props {
  children: React.ReactNode;
  isDeprecated?: boolean;
}

export const ProvideUploadFile = ({ children, isDeprecated = false }: Props) => {
  const uploadFiles = useProvideUploadFile(isDeprecated);
  return <UploadFileContext.Provider value={{ ...uploadFiles, isDeprecated }}>{children}</UploadFileContext.Provider>;
};

export const useProvideUploadFile = (isDeprecated: boolean) => {
  const [uploads, setUploads] = useState<UploadFileMeta[]>([]);
  const [isPendingFilesConversion, setIsPendingFilesConversion] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [entityLabel, setEntityLabel] = useState<string | undefined>(undefined);

  const onDeleteFile = useCallback(
    (file: File) => {
      setUploads(uploads.filter((u) => u.file.name !== file.name));
    },
    [uploads]
  );

  const onDeleteAllFiles = useCallback(() => {
    setUploads([]);
  }, [setUploads]);

  const onOpenUploadManager = useCallback((name?: string) => {
    setEntityLabel(name ?? '');
    setIsOpen(true);
  }, []);

  const onCloseUploadManager = useCallback(() => {
    onDeleteAllFiles();
    setIsOpen(false);
    setEntityLabel(undefined);
  }, [onDeleteAllFiles]);

  const onSelectFiles = useCallback(
    async (files: File[], entityId?: string) => {
      setIsPendingFilesConversion(true);
      const existing = new Set(uploads.map((u) => u.file.name));
      const availableSlots = ALLOWED_FILES_COUNT - uploads.length;
      const filteredFiles = files.filter((f) => !existing.has(f.name));
      const filesToAddRaw = filteredFiles.slice(0, availableSlots);
      const skippedCount = filteredFiles.length - filesToAddRaw.length;

      if (skippedCount > 0) {
        toast.info(`You can only upload up to ${ALLOWED_FILES_COUNT} files at a time`);
      }

      const processedFiles: File[] = [];
      for (const file of filesToAddRaw) {
        if (isHeicLikeFile(file)) {
          try {
            const [webp] = await convertHeicFilesToWebp([file]);
            processedFiles.push(webp);
          } catch {
            toast.error(`Could not convert ${file.name} to WebP for preview.`);
          }
        } else {
          processedFiles.push(file);
        }
      }

      setUploads((prev) => {
        const existingNow = new Set(prev.map((u) => u.file.name));
        const slotsNow = ALLOWED_FILES_COUNT - prev.length;
        const toAdd = processedFiles.filter((f) => !existingNow.has(f.name)).slice(0, slotsNow);

        const newUploads = toAdd.map((file) => ({
          file,
          progress: 0,
          status: 'pending' as UploadStatus,
          entityId,
        }));

        return [...prev, ...newUploads];
      });
      setIsPendingFilesConversion(false);
    },
    [uploads]
  );

  const onCropImage = useCallback((blob: Blob, fileName: string) => {
    setUploads((prev) => {
      const fileIndex = prev.findIndex((upload) => upload.file.name === fileName);
      if (fileIndex === -1) return prev;
      const newFileName = `${fileName.split('.')[0]}-(cropped)-${Date.now()}.${fileName.split('.')[1]}`;
      const newFile = new File([blob], newFileName, { type: blob.type });
      return prev.map((upload, index) => (index === fileIndex ? { ...upload, file: newFile } : upload));
    });
  }, []);

  const uploadFileWithProgress = useCallback(
    async (
      file: File,
      onProgress: (percent: number) => void,
      controller: AbortController,
      finalizeUploadRequest: FinalizeUploadRequest,
      entityId?: string | number
    ): Promise<void> => {
      const fileExtension = getFileExtension(file.name);

      return new Promise((resolve, reject) => {
        let totalProgress = 0;
        const STEPS = 3;
        const updateProgress = (stepProgress: number) => {
          const stepWeight = 100 / STEPS;
          const currentStepProgress = (stepProgress * stepWeight) / 100;
          totalProgress = Math.min(Math.round(totalProgress + currentStepProgress), 100);
          onProgress(totalProgress);
        };

        const getPresignedUrl = async (authToken: string): Promise<PresignedDataResponse> => {
          const presignedAuditUrl = isDeprecated
            ? `/api/gallery/presigned-url?file_extension=${fileExtension}`
            : '/api/storage/presigned-url';

          await sendPreRequestAuditIfMatched(presignedAuditUrl, {
            method: isDeprecated ? 'GET' : 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: isDeprecated ? undefined : JSON.stringify({ file_extension: fileExtension }),
          });

          const xhr1 = new XMLHttpRequest();
          if (isDeprecated) {
            xhr1.open(
              'GET',
              `${process.env.NEXT_PUBLIC_API_URL}/api/gallery/presigned-url?file_extension=${fileExtension}`,
              true
            );
            xhr1.setRequestHeader('Authorization', `Bearer ${authToken}`);
            xhr1.setRequestHeader('Content-Type', 'application/json');
          } else {
            xhr1.open('POST', `${process.env.NEXT_PUBLIC_API_URL}/api/storage/presigned-url`, true);
          }
          xhr1.setRequestHeader('Authorization', `Bearer ${authToken}`);
          xhr1.setRequestHeader('Content-Type', 'application/json');

          return new Promise<PresignedDataResponse>((resolveStep1, rejectStep1) => {
            xhr1.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                updateProgress((event.loaded / event.total) * 100);
              }
            };

            xhr1.onload = () => {
              if (xhr1.status === 401) {
                rejectStep1(Object.assign(new Error('Unauthorized'), { status: 401 }));
                return;
              }

              if (xhr1.status !== 200) {
                rejectStep1(new Error('Failed to get presigned URL'));
                toast.error(`Failed to get presigned URL: ${file.name}`);
                return;
              }

              let presignedData: { url?: string; upload_url?: string };
              try {
                const parsed = JSON.parse(xhr1.responseText) as { data?: typeof presignedData };
                if (!parsed?.data) {
                  rejectStep1(new Error('Invalid presigned URL response'));
                  toast.error(`Failed to get presigned URL: ${file.name}`);
                  return;
                }
                presignedData = parsed.data;
              } catch {
                rejectStep1(new Error('Invalid presigned URL response'));
                toast.error(`Failed to get presigned URL: ${file.name}`);
                return;
              }
              updateProgress(100);
              resolveStep1(presignedData);
            };

            xhr1.onerror = () => rejectStep1(new Error('Upload error'));
            xhr1.onabort = () => rejectStep1(new Error('Upload canceled'));
            controller.signal.addEventListener('abort', () => xhr1.abort());
            if (isDeprecated) {
              xhr1.send();
            } else {
              xhr1.send(JSON.stringify({ file_extension: fileExtension }));
            }
          });
        };

        const uploadToPresignedUrl = (presignedData: PresignedDataResponse): Promise<PresignedDataResponse> => {
          const xhr2 = new XMLHttpRequest();
          xhr2.open('PUT', presignedData.url || presignedData.upload_url || '', true);
          xhr2.setRequestHeader('Content-Type', file.type);

          return new Promise((resolveStep2, rejectStep2) => {
            xhr2.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                updateProgress((event.loaded / event.total) * 100);
              }
            };

            xhr2.onload = () => {
              if (xhr2.status !== 200) {
                rejectStep2(new Error('Upload to presigned URL failed'));
                toast.error(`Failed to upload to image: ${file.name}`);
                return;
              }
              updateProgress(100);
              resolveStep2(presignedData);
            };

            xhr2.onerror = () => rejectStep2(new Error('Upload error'));
            xhr2.onabort = () => rejectStep2(new Error('Upload canceled'));
            controller.signal.addEventListener('abort', () => xhr2.abort());
            xhr2.send(file);
          });
        };

        const runFinalize = (presignedData: PresignedDataResponse, authToken: string) =>
          finalizeUploadRequest({
            key: presignedData.key,
            storage_id: presignedData.storage_id,
            fileExtension: fileExtension,
            entityId,
            token: authToken,
            signal: controller.signal,
            title: file.name,
            onProgress: updateProgress,
          });

        const executeUploadSteps = async (authToken: string) => {
          const presignedData = await getPresignedUrl(authToken);
          const uploadedData = await uploadToPresignedUrl(presignedData);
          await runFinalize(uploadedData, authToken);
        };

        const isUnauthorizedError = (error: unknown): error is Error & { status: number } =>
          error instanceof Error && (error as Error & { status: number }).status === 401;

        const runUploadWithAuthRetry = async () => {
          const initialToken = getAccessToken();
          if (!initialToken) {
            throw new Error('No auth token provided');
          }

          try {
            await executeUploadSteps(initialToken);
          } catch (error) {
            if (!isUnauthorizedError(error)) {
              throw error;
            }

            const newToken = await refreshAccessToken();
            await executeUploadSteps(newToken);
          }
        };

        void runUploadWithAuthRetry().then(resolve).catch(reject);
      });
    },
    [isDeprecated]
  );

  const onUploadFiles = useCallback(
    async (finalizeUploadRequest: FinalizeUploadRequest) => {
      setUploads((prev) =>
        prev.map((item) => ({
          ...item,
          status: 'uploading',
          controller: new AbortController(),
        }))
      );

      let currentUploads: UploadFileMeta[] = [];
      setUploads((prev) => {
        currentUploads = prev;
        return prev;
      });

      await new Promise((resolve) => setTimeout(resolve, 0));

      const uploadsToProcess = currentUploads.length > 0 ? currentUploads : uploads;

      const uploadPromises = uploadsToProcess.map(async (item) => {
        const controller = item.controller ?? new AbortController();
        let fileToUpload = item.file;
        const entityId = item.entityId;

        try {
          if (isHeicLikeFile(item.file)) {
            const convertedImages = await convertHeicFilesToWebp([item.file]);
            fileToUpload = convertedImages[0];
          } else if (item.file.type.startsWith('image/')) {
            const convertedImages = await getConvertedToWebpImages([item.file]);
            fileToUpload = convertedImages[0];
          }

          await uploadFileWithProgress(
            fileToUpload,
            (percent) => {
              setUploads((prev) => prev.map((u) => (u.file.name === item.file.name ? { ...u, progress: percent } : u)));
            },
            controller,
            finalizeUploadRequest,
            entityId
          );

          setUploads((prev) => prev.map((u) => (u.file.name === item.file.name ? { ...u, status: 'success' } : u)));
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_error) {
          const isCanceled = controller.signal.aborted;
          setUploads((prev) =>
            prev.map((u) => (u.file.name === item.file.name ? { ...u, status: isCanceled ? 'canceled' : 'error' } : u))
          );
        }
      });

      try {
        await Promise.all(uploadPromises);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        toast.error(`Failed to upload files`);
      }
    },
    [uploads, uploadFileWithProgress]
  );

  const onCancelUpload = useCallback((fileName: string) => {
    setUploads((prev) => {
      const updated = prev.map((item) => {
        if (item.file.name === fileName) {
          if (item.controller) {
            item.controller.abort();
          }
          return { ...item, status: 'canceled' as UploadStatus };
        }
        return item;
      });
      return updated;
    });
  }, []);

  const onCancelAllUploads = useCallback(() => {
    setUploads((prev) =>
      prev.map((u) => {
        if (u.controller) {
          u.controller.abort();
        }
        return { ...u, status: 'canceled' as UploadStatus };
      })
    );
  }, []);

  const files = useMemo(() => uploads.map((upload) => upload.file), [uploads]);

  return {
    uploads,
    files,
    onDeleteFile,
    onDeleteAllFiles,
    onSelectFiles,
    onUploadFiles,
    onCancelUpload,
    onCancelAllUploads,
    onOpenUploadManager,
    onCloseUploadManager,
    isOpenUploadManager: isOpen,
    isPendingFilesConversion,
    entityLabel,
    onCropImage,
  };
};

export const useUploadFile = () => {
  return useContext(UploadFileContext);
};